<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, toRaw, inject } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { useForm } from "vee-validate"

import { useFetchWrapper } from "@core/composables"
import { ServiceError } from "@core/exceptions"
import type { UserAlarmSettings } from "@types"
import { Services } from "@services"
import { useI18n } from "vue-i18n"
import * as yup from "yup"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            save: "Sauvegarder",
            saveSuccess: "Vos paramètres ont été enregistrés avec succès",
            saveError: "Impossible de sauvegarder les paramètres",
            loadError: "Erreur lors du chargement",
            validationError: "Le formulaire contient des erreurs",

            error: {
                validationErrorMessage:
                    "Veuillez sélectionner une plage horaire valide",
                validationErrorMessage2:
                    "La plage horaire doit être valide (l'heure de début ne peut pas être identique à l'heure de fin)",
            },
            form: {
                restDays: "Jours de repos",
                receiveAlarmNextTimeAvailable:
                    "Recevoir l'alarme la prochaine fois que je suis disponible",
                noAlarmBetweenHoursStart: "Heure de début",
                noAlarmBetweenHoursEnd: "Heure de fin",
                select_placeholder: "Sélectionner...",
                doNotDisturbEnabled: "Ne pas recevoir de notification de :",
                isPaused: "Mettre en pause les notifications par email",
            },
        },
    },
})
// ================================
// COMPOSABLES & STORES
// ================================

/**
 * Store d'authentification pour accéder aux données utilisateur
 */
const authStore = useAuthStore()

const { $updateCurrentUser } = inject<Services>("services")!

// ================================
// TYPES & INTERFACES
// ================================

// (Aucun type personnalisé nécessaire - utilisation des types existants)

// ================================
// ÉTAT DES DONNÉES
// ================================

/**
 * Snapshot du formulaire pour détecter les changements
 */
const snapshotForm = ref<string | null>(null)

// ================================
// DONNÉES DE CONFIGURATION
// ================================

/**
 * Liste des heures de 0 à 23 pour la sélection de plage horaire
 */
const hoursList = Array.from({ length: 24 }, (_, i) => ({
    label: `${i}h`,
    value: i,
}))

/**
 * Liste des jours de la semaine pour la sélection des jours de repos
 */
const weekDays = [
    { label: "Lundi", value: "MONDAY" },
    { label: "Mardi", value: "TUESDAY" },
    { label: "Mercredi", value: "WEDNESDAY" },
    { label: "Jeudi", value: "THURSDAY" },
    { label: "Vendredi", value: "FRIDAY" },
    { label: "Samedi", value: "SATURDAY" },
    { label: "Dimanche", value: "SUNDAY" },
]

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Détermine si le bouton de sauvegarde doit être désactivé
 * @returns true si le formulaire est en cours de chargement ou de soumission
 */
const getIsSaveDisabled = computed((): boolean => {
    return (
        isFetchingSettings.value ||
        isSubmitting.value ||
        !getHasDataChanged.value
    )
})

/**
 * Détermine si le formulaire a été modifié
 * @returns true si des modifications ont été apportées, false sinon
 */
const getHasDataChanged = computed((): boolean => {
    if (!snapshotForm.value) return false
    const currentForm = JSON.stringify({
        pause: modelPause.value,
        noAlarmBetweenHoursAvailable: modelNoAlarmBetweenHoursAvailable.value,
        noAlarmBetweenHours: modelNoAlarmBetweenHours.value,
        noAlarmOnDays: modelNoAlarmOnDays.value,
        receiveAlarmNextTimeAvailable: modelReceiveAlarmNextTimeAvailable.value,
    })
    return snapshotForm.value !== currentForm
})

// ================================
// CHARGEMENT DES DONNÉES
// ================================

/**
 * Initialise le formulaire avec les données du store
 */
function initializeForm() {
    const alarme = authStore.alarme ?? null
    const notifications = alarme?.notifications ?? {
        pause: false,
        noAlarmBetweenHoursAvailable: false,
        noAlarmBetweenHours: [null, null],
        noAlarmOnDays: [],
        receiveAlarmNextTimeAvailable: false,
    }

    modelPause.value = notifications.pause
    modelNoAlarmBetweenHoursAvailable.value =
        notifications.noAlarmBetweenHoursAvailable
    modelNoAlarmBetweenHours.value = notifications.noAlarmBetweenHours
    modelNoAlarmOnDays.value = notifications.noAlarmOnDays
    modelReceiveAlarmNextTimeAvailable.value =
        notifications.receiveAlarmNextTimeAvailable
    snapshotForm.value = JSON.stringify(
        toRaw({
            pause: modelPause.value,
            noAlarmBetweenHoursAvailable:
                modelNoAlarmBetweenHoursAvailable.value,
            noAlarmBetweenHours: modelNoAlarmBetweenHours.value,
            noAlarmOnDays: modelNoAlarmOnDays.value,
            receiveAlarmNextTimeAvailable:
                modelReceiveAlarmNextTimeAvailable.value,
        })
    )
}

/**
 * Indique si les données d'alarme sont chargées
 */
const getIsAlarmDataLoaded = computed((): boolean => {
    return !!authStore.userId
})

/**
 * Indique si le chargement est en cours
 */
const isFetchingSettings = computed((): boolean => {
    return authStore.isFetchingUser
})

/**
 * Indique s'il y a une erreur de chargement
 */
const hasErrorLoadingSettings = computed((): boolean => {
    return authStore.hasErrorUser
})

/**
 * Erreur de chargement
 */
const errorLoadingSettings = computed(() => {
    return authStore.errorUser
})

// ================================
// FORMULAIRE & VALIDATION
// ================================

/**
 * Schéma de validation du formulaire
 */
const schema = yup.object({
    pause: yup.boolean().required("Le champ doit être rempli"),
    noAlarmBetweenHoursAvailable: yup
        .boolean()
        .required("Le champ doit être rempli"),
    noAlarmBetweenHours: yup.array().when("noAlarmBetweenHoursAvailable", {
        is: true,
        then: (schema) =>
            schema
                .required(
                    "Veuillez sélectionner une heure de début et une heure de fin"
                )
                .of(yup.number())
                .min(2, "Le champ doit contenir au moins 2 éléments")
                .max(2, "Le champ doit contenir au plus 2 éléments"),
        otherwise: (schema) => schema.notRequired(),
    }),
    noAlarmOnDays: yup
        .array()
        .of(yup.string())
        .required("Le champ doit être rempli"),
    receiveAlarmNextTimeAvailable: yup
        .boolean()
        .required("Le champ doit être rempli"),
})

const { errors, defineField, handleSubmit } = useForm({
    validationSchema: schema,
})

const [modelPause] = defineField("pause")
const [modelNoAlarmBetweenHoursAvailable] = defineField(
    "noAlarmBetweenHoursAvailable"
)
const [modelNoAlarmBetweenHours] = defineField("noAlarmBetweenHours")
const [modelNoAlarmOnDays] = defineField("noAlarmOnDays")
const [modelReceiveAlarmNextTimeAvailable] = defineField(
    "receiveAlarmNextTimeAvailable"
)

// ================================
// SAUVEGARDE
// ================================

const timeoutSuccess = ref<NodeJS.Timeout | null>(null)
/**
 * Sauvegarde les paramètres d'alarme modifiés
 */
const {
    fetch: onUpdateUserAlarmSetting,
    isFetching: isSubmitting,
    hasError: hasErrorSubmitting,
    error: errorSubmitting,
    isSuccess: isSuccessSubmitting,
} = useFetchWrapper(async () => {
    if (timeoutSuccess.value) {
        clearTimeout(timeoutSuccess.value)
    }

    const settingsToSave: UserAlarmSettings = {
        pause: modelPause.value,
        noAlarmBetweenHoursAvailable: modelNoAlarmBetweenHoursAvailable.value,
        noAlarmBetweenHours: modelNoAlarmBetweenHours.value,
        noAlarmOnDays: modelNoAlarmOnDays.value,
        receiveAlarmNextTimeAvailable: modelReceiveAlarmNextTimeAvailable.value,
    }

    const payload = { alarme: { notifications: settingsToSave } }
    const updatedUser = await $updateCurrentUser(payload)

    const returnedNotifications =
        updatedUser?.alarme?.notifications ?? settingsToSave

    // Met à jour le formulaire avec les données retournées par l'API
    modelPause.value = returnedNotifications.pause
    modelNoAlarmBetweenHoursAvailable.value =
        returnedNotifications.noAlarmBetweenHoursAvailable
    modelNoAlarmBetweenHours.value = returnedNotifications.noAlarmBetweenHours
    modelNoAlarmOnDays.value = returnedNotifications.noAlarmOnDays
    modelReceiveAlarmNextTimeAvailable.value =
        returnedNotifications.receiveAlarmNextTimeAvailable

    // Met à jour le store avec les nouvelles données d'alarme
    authStore.updateUserInfo({
        alarme: { notifications: returnedNotifications },
    })

    // Met à jour le snapshot après un court délai pour permettre l'affichage du message de succès
    setTimeout(() => {
        snapshotForm.value = JSON.stringify(
            toRaw({
                pause: modelPause.value,
                noAlarmBetweenHoursAvailable:
                    modelNoAlarmBetweenHoursAvailable.value,
                noAlarmBetweenHours: modelNoAlarmBetweenHours.value,
                noAlarmOnDays: modelNoAlarmOnDays.value,
                receiveAlarmNextTimeAvailable:
                    modelReceiveAlarmNextTimeAvailable.value,
            })
        )
    }, 1000)

    timeoutSuccess.value = setTimeout(() => {
        isSuccessSubmitting.value = false
    }, 3000)
})

const onInvalidSubmit = () => {
    errorSubmitting.value = new ServiceError({
        message: t("validationError"),
        code: "VALIDATION_ERROR",
    })
}

const onSubmit = handleSubmit(onUpdateUserAlarmSetting, onInvalidSubmit)

// ================================
// CYCLE DE VIE
// ================================

/**
 * Surveille les changements de userId et initialise le formulaire
 */
watch(
    () => authStore.userId,
    (userId) => {
        if (userId && getIsAlarmDataLoaded.value) {
            initializeForm()
        }
    },
    { immediate: true }
)

/**
 * Surveille les changements des données d'alarme dans le store
 */
watch(
    () => authStore.alarme,
    () => {
        if (getIsAlarmDataLoaded.value && !snapshotForm.value) {
            initializeForm()
        }
    },
    { immediate: true, deep: true }
)
</script>

<template>
    <BaseCard v-if="getIsAlarmDataLoaded && !isFetchingSettings">
        <template #content>
            <div class="form-user-alarm-setting flex flex-col gap-4">
                <!-- ================================ -->
                <!-- ÉTAT DE CHARGEMENT -->
                <!-- ================================ -->

                <!-- ================================ -->
                <!-- MESSAGE D'ERREUR DE CHARGEMENT -->
                <!-- ================================ -->
                <BaseMessage
                    v-if="hasErrorLoadingSettings"
                    severity="error"
                    class="mb-4"
                >
                    {{ errorLoadingSettings?.message || t("loadError") }}
                </BaseMessage>

                <!-- ================================ -->
                <!-- FORMULAIRE PRINCIPAL -->
                <!-- ================================ -->
                <form
                    v-else
                    class="form-user-alarm-setting__form flex flex-col gap-6"
                    @submit.prevent="onSubmit"
                >
                    <!-- ================================ -->
                    <!-- PAUSE DES NOTIFICATIONS -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="isPaused"
                        :label="t('form.isPaused')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            v-model="modelPause"
                            input-id="isPaused"
                            :invalid="!!errors.pause"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- NE PAS DÉRANGER -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="doNotDisturbEnabled"
                        :label="t('form.doNotDisturbEnabled')"
                        unfloat
                        orientation="horizontal"
                    >
                        <BaseToggleSwitch
                            v-model="modelNoAlarmBetweenHoursAvailable"
                            input-id="doNotDisturbEnabled"
                            :invalid="!!errors.noAlarmBetweenHoursAvailable"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- PLAGE HORAIRE -->
                    <!-- ================================ -->
                    <div
                        v-if="modelNoAlarmBetweenHoursAvailable"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4 ml-0 md:ml-8"
                    >
                        <BaseInputWrapper
                            input-id="noAlarmBetweenHoursStart"
                            :label="t('form.noAlarmBetweenHoursStart')"
                            unfloat
                        >
                            <BaseSelect
                                v-model="modelNoAlarmBetweenHours[0]"
                                input-id="noAlarmBetweenHoursStart"
                                :options="hoursList"
                                option-label="label"
                                option-value="value"
                                :placeholder="t('form.select_placeholder')"
                                :invalid="!!errors.noAlarmBetweenHours"
                            />
                        </BaseInputWrapper>

                        <BaseInputWrapper
                            input-id="noAlarmBetweenHoursEnd"
                            :label="t('form.noAlarmBetweenHoursEnd')"
                            unfloat
                        >
                            <BaseSelect
                                v-model="modelNoAlarmBetweenHours[1]"
                                input-id="noAlarmBetweenHoursEnd"
                                :options="hoursList"
                                option-label="label"
                                option-value="value"
                                :placeholder="t('form.select_placeholder')"
                                :invalid="!!errors.noAlarmBetweenHours"
                            />
                        </BaseInputWrapper>
                    </div>

                    <!-- ================================ -->
                    <!-- REPORT AUTOMATIQUE -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="receiveAlarmNextTimeAvailable"
                        :label="t('form.receiveAlarmNextTimeAvailable')"
                        unfloat
                        orientation="horizontal"
                        :error="errors.receiveAlarmNextTimeAvailable"
                    >
                        <BaseToggleSwitch
                            v-model="modelReceiveAlarmNextTimeAvailable"
                            input-id="receiveAlarmNextTimeAvailable"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- JOURS DE REPOS -->
                    <!-- ================================ -->
                    <BaseInputWrapper
                        input-id="restDays"
                        :label="t('form.restDays')"
                        unfloat
                        :error="errors.noAlarmOnDays"
                    >
                        <BaseSelectButton
                            v-model="modelNoAlarmOnDays"
                            :options="weekDays"
                            option-label="label"
                            option-value="value"
                            multiple
                            :allow-empty="true"
                            :invalid="!!errors.noAlarmOnDays"
                        />
                    </BaseInputWrapper>

                    <!-- ================================ -->
                    <!-- ACTIONS -->
                    <!-- ================================ -->
                    <div class="flex flex-col gap-4 pt-4">
                        <BaseButton
                            class="w-fit mx-auto"
                            type="submit"
                            :label="t('save')"
                            :loading="isSubmitting"
                            :disabled="getIsSaveDisabled"
                        />
                        <!-- ================================ -->
                        <!-- MESSAGE D'ERREUR DE SOUMISSION -->
                        <!-- ================================ -->
                        <BaseMessage
                            v-if="hasErrorSubmitting"
                            severity="error"
                            class="mb-4"
                        >
                            {{ errorSubmitting?.message || t("saveError") }}
                        </BaseMessage>

                        <!-- ================================ -->
                        <!-- MESSAGE DE SUCCÈS -->
                        <!-- ================================ -->
                        <BaseMessage
                            severity="success"
                            v-if="isSuccessSubmitting"
                            class="mb-4"
                        >
                            {{ t("saveSuccess") }}
                        </BaseMessage>
                    </div>
                </form>
            </div>
        </template>
    </BaseCard>
    <BaseSkeleton v-else width="100%" height="40rem" />
</template>
